import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Truck, Shield, RotateCcw, Headphones, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import EditableText from "@/components/EditableText";

const features = [
  {
    icon: Truck,
    title: "משלוח מהיר",
    desc: "1-5 ימי עסקים",
    color: "text-primary",
    bg: "bg-primary/10",
    glowHsl: "var(--primary)",
    detail: "אנחנו שולחים את כל ההזמנות תוך 1-5 ימי עסקים ישירות עד הבית. המשלוחים מבוצעים באמצעות שליחים מקצועיים עם מעקב בזמן אמת, כך שתמיד תדעו איפה החבילה שלכם.",
  },
  {
    icon: Shield,
    title: "תשלום מאובטח",
    desc: "סליקה מאובטחת",
    color: "text-success",
    bg: "bg-success/10",
    glowHsl: "142 71% 45%",
    detail: "התשלום מתבצע באמצעות מערכת סליקה מאובטחת בתקן SSL. פרטי כרטיס האשראי אינם נשמרים במערכות האתר.",
  },
  {
    icon: RotateCcw,
    title: "החזרה קלה",
    desc: "החזרה לכתובת החנות",
    color: "text-accent",
    bg: "bg-accent/10",
    glowHsl: "var(--accent)",
    detail: "לא מרוצים? אין בעיה! ניתן להחזיר כל מוצר תוך 30 יום מיום הקנייה לכתובת החנות. עלות המשלוח חזרה היא על חשבון הלקוח.",
  },
  {
    icon: Headphones,
    title: "תמיכה אישית",
    desc: "וואטסאפ 8:00-22:00",
    color: "text-hot",
    bg: "bg-hot/10",
    glowHsl: "0 84% 60%",
    detail: "שירות הלקוחות זמין בוואטסאפ בכל יום בין 8:00 ל-22:00.",
  },
];

const FeatureStrip = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const isMobile = useIsMobile();

  return (
    <section className="py-5 md:py-6 border-b border-border/30 bg-gradient-to-b from-background to-muted/30 relative overflow-hidden">
      {/* Subtle neon glow background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-32 rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(circle, hsl(var(--primary)), transparent 70%)" }}
        />
        <div className="absolute bottom-0 right-1/4 w-48 h-24 rounded-full opacity-[0.03]"
          style={{ background: "radial-gradient(circle, hsl(var(--accent)), transparent 70%)" }}
        />
      </div>

      <div className="container relative">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-3">
          {features.map((feat, i) => (
            <motion.div
              key={feat.title}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              whileHover={{ scale: 1.03, y: -2 }}
              className="flex items-center gap-3 p-3 md:p-4 rounded-xl bg-card/80 backdrop-blur-sm border border-border/30 hover:border-primary/30 transition-all duration-300 cursor-pointer group"
              style={{ transition: "all 0.3s" }}
              onClick={() => setOpenIdx(i)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 20px hsl(${feat.glowHsl} / 0.15), 0 4px 20px hsl(${feat.glowHsl} / 0.1), inset 0 1px 0 hsl(${feat.glowHsl} / 0.1)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "";
              }}
            >
              <div className={`w-10 h-10 rounded-xl ${feat.bg} flex items-center justify-center flex-shrink-0 group-hover:shadow-[0_0_14px_hsl(var(--primary)/0.25)] transition-all duration-300`}>
                <feat.icon size={19} className={`${feat.color} group-hover:scale-110 group-hover:drop-shadow-[0_0_6px_hsl(var(--primary)/0.5)] transition-all duration-200`} />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors duration-200"><EditableText contentKey={`feature-title-${i}`} defaultText={feat.title} /></p>
                <p className="text-[11px] text-muted-foreground leading-snug"><EditableText contentKey={`feature-desc-${i}`} defaultText={feat.desc} /></p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Detail popup overlay */}
      <AnimatePresence>
        {openIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setOpenIdx(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 350 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm bg-card border border-border/50 rounded-2xl p-6 shadow-2xl"
              dir="rtl"
            >
              <button
                onClick={() => setOpenIdx(null)}
                className="absolute top-3 left-3 w-8 h-8 rounded-full bg-muted/80 flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
              >
                <X size={16} />
              </button>

              {(() => {
                const feat = features[openIdx];
                return (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${feat.bg} flex items-center justify-center`}
                        style={{ boxShadow: `0 0 20px hsl(${feat.glowHsl} / 0.25)` }}
                      >
                        <feat.icon size={24} className={feat.color} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">{feat.title}</h3>
                        <p className="text-xs text-muted-foreground">{feat.desc}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feat.detail}
                    </p>
                  </div>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FeatureStrip;
